import { Affix, Button, Empty, Tooltip } from 'antd';
import React from 'react';
import Sider from 'antd/es/layout/Sider';
import classes from './AnalysisSider.module.scss'
import { useAppDispatch, useAppSelector } from 'store/hook';
import Spinner from 'components/Spinner/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartPie } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { checkAnalysis, getAnalysisOfDocument } from 'store/reducers/aiFunctionsCreator';
import { setAnalysisData, setAnalysisProcessStatus, setIsOpenAnalysisSider, setIsSendToAnalysisOpen } from 'store/slices/aiFunctionsDataSlice';
import SendModal from './SendModal/SendModal';
import AnalyticsReport from './AnalyticsReport/AnalyticsReport';
import Assets from 'assets/icons';
import { simulateProgress } from './AnalyticsReport/simulateProgress';

let currentPercent = 1;
let intervalId: ReturnType<typeof setInterval> | null = null;
export const widthOfSiderAnalytics = [580, 450, 320, 0];

const AnalysisSider = ({wrapRef}: {wrapRef: React.RefObject<HTMLElement>}) => {
  const [percent, setPercent] = React.useState(0);
  const [tooSmallWidth, setTooSmallWidth] = React.useState(false);

  const {isOpenAnalysisSider, analysisSiderWidth, analysisData, isSendToAnalysisOpen,
    analysisProcessStatus } = useAppSelector((state) => state.aiFunctions);
  const { selectedDocument } = useAppSelector((state) => state.documents);
  
  const actualWidthIndex = widthOfSiderAnalytics.findIndex((el) => el === analysisSiderWidth);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const isNeedToDisableIncreaseBtn = analysisSiderWidth === 580 || tooSmallWidth;

  const findFreeSpace = () => {
    const freeSpace = window.innerWidth - Number(localStorage.getItem('sideBarWidth')) - widthOfSiderAnalytics[actualWidthIndex];
    setTooSmallWidth(freeSpace < 500);
  };
  React.useEffect(() => {
    window.addEventListener('resize', findFreeSpace);
    findFreeSpace();
    return () => window.removeEventListener('resize', findFreeSpace);
  }, [actualWidthIndex]);

  React.useEffect(() => {
    if (!wrapRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        findFreeSpace()
      };
    });
    resizeObserver.observe(wrapRef.current);
    return () => resizeObserver.disconnect();
  }, [wrapRef.current]);

  React.useEffect(() => {
    const stop = simulateProgress(analysisProcessStatus, setPercent, intervalId, currentPercent);
    return stop;
  }, [analysisProcessStatus]);

  React.useEffect(() => {
    return () => {
      dispatch(setAnalysisData(null));
      dispatch(setAnalysisProcessStatus('checking'));
      setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
      }, 3000);
    }
  },[]);

  React.useEffect(() => {
    if (analysisProcessStatus === 'downloading_report') {
      dispatch(getAnalysisOfDocument({ document: selectedDocument!, t }));
      setTimeout(() => {
        if (intervalId) clearInterval(intervalId);
      }, 3000);
    }
  },[analysisProcessStatus]);

  React.useEffect(() => {
    if (isOpenAnalysisSider && !analysisData) {
      setTimeout(() => {
        dispatch(checkAnalysis({ document: selectedDocument!, t }));
      }, 1000);
    }
  },[analysisData, isOpenAnalysisSider]);

  const openSendToAnalysisModal = () => {
    dispatch(setIsSendToAnalysisOpen(true));
  };

  const lessAnalysis = () => {
    dispatch(setIsOpenAnalysisSider({ open: actualWidthIndex === 2 ? false : true, width: widthOfSiderAnalytics[actualWidthIndex + 1] }));
  };

  const moreAnalysis = () => {
    dispatch(setIsOpenAnalysisSider({ open: true, width: widthOfSiderAnalytics[actualWidthIndex - 1] }));
  };

  const downloadReport = () => {
    const blob = new Blob([analysisData?.file], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('Documents.details.analysisTab.reportFile')} «${selectedDocument?.name}».txt`;
    a.click();

    URL.revokeObjectURL(url);
  }

  const cases = {
    unavailable: (
      <div className={classes.holder}>
        <Empty
          className={classes.noTrascription}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{t('Documents.details.analysisTab.unavailable')}</span>}
        />
      </div>
    ),
    not_found: (
      <div className={classes.holder}>
        <Empty
          className={classes.noTrascription}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{t('Documents.details.analysisTab.empty')}</span>}
        />
        <Button
          icon={<FontAwesomeIcon icon={faChartPie} />}
          className={classes.addTrascription}
          type='primary'
          onClick={openSendToAnalysisModal}
        >
          {t('Documents.details.analysisTab.send')}
        </Button>
      </div>
    ),
    in_progress: (
      <div className={classes.holder}>
        <Spinner size={60} withLogo={true} height={60}/>
        {/* <Progress
          type='circle'
          // strokeColor={colors.primary100}
          percent={percent}
          size={50}
        /> */}
        <div className={classes.text}>{t(`Documents.details.analysisTab.${analysisProcessStatus}`, {percent})}</div>
      </div>
    ),
    ready: <AnalyticsReport />,
  };

  return (
    <Affix>
      <Sider
        theme='light'
        width={analysisSiderWidth || 0}
        collapsedWidth={0}
        collapsed={!isOpenAnalysisSider}
        className={classes.analysisSider}
      >
        <div className={classNames(classes.siderWrap, isOpenAnalysisSider ? 'null' : classes.DNone)}>
          <div className={classes.title}>{t('Documents.details.analysisTab.title')}</div>
          <div className={classes.content}>{cases[String(analysisData?.status || 'in_progress') as keyof typeof cases]}</div>
          <div className={classes.footer}>
            <Tooltip title={t('Documents.details.analysisTab.increase')}>
              <Button disabled={isNeedToDisableIncreaseBtn} className={classes.sizeBtn} size='small' onClick={moreAnalysis} icon={<Assets.DoubleLeftIcon />}/>
            </Tooltip>
            {analysisData?.status === 'ready' && (
              <Button size='small' type='primary' onClick={openSendToAnalysisModal}>
                {t('Documents.details.analysisTab.newAnalys')}
              </Button>
            )}
            {analysisData?.file && true && (
              <Button
                className={classes.dwnlBtn}
                size='small'
                // icon={<Assets.DownloadIcon />}
                onClick={downloadReport}
              >
                {t('Documents.details.analysisTab.downloadReport')}
              </Button>
            )}

            <Tooltip placement='topRight' title={t(`Documents.details.analysisTab.${analysisSiderWidth === 320 ? 'hide' : 'decrease'}`)}>
              <Button className={classes.sizeBtn} size='small' onClick={lessAnalysis} icon={<Assets.DoubleRightIcon />}/>
            </Tooltip>
          </div>
        </div>
        {isSendToAnalysisOpen && (
          <React.Suspense fallback={<div />}>
            <SendModal
              open={isSendToAnalysisOpen}
            />
          </React.Suspense>
        )}
      </Sider>
    </Affix>
  );
}

export default AnalysisSider;
